import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ErrorBoundary } from './ErrorBoundary'
import { TabIcon, type TabIconName } from './TabIcons'

export interface TabDef {
  key: string
  label: string
  /** Rendered lazily, so a tab's data is fetched only when it is first opened —
      the same deferred loading the legacy `li>a` click handler did. */
  render: () => ReactNode
  hidden?: boolean
  /** Glyph shown before the label, as the legacy tabs had. Defaults to the key. */
  icon?: TabIconName
}

interface TabsProps {
  tabs: TabDef[]
  initialKey?: string
  /**
   * localStorage key the user's tab order is saved under. Same mechanism as the
   * Sales module's LeadDetail (`leadDetailTabsOrder`), so each screen keeps its
   * own sequence. Omit to disable reordering.
   */
  orderStorageKey?: string
}

/** Reads a saved order, ignoring anything malformed or stale. */
function readSavedOrder(storageKey: string | undefined): string[] {
  if (!storageKey) return []
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

/**
 * Orders the tabs by the saved sequence. Keys that are no longer defined are
 * dropped and newly added tabs fall in at the end, so a saved order never hides
 * a tab that the app gained later.
 */
function applyOrder(tabs: TabDef[], order: string[]): TabDef[] {
  if (order.length === 0) return tabs
  const byKey = new Map(tabs.map((t) => [t.key, t]))
  const ordered: TabDef[] = []
  for (const key of order) {
    const tab = byKey.get(key)
    if (tab) {
      ordered.push(tab)
      byKey.delete(key)
    }
  }
  return [...ordered, ...byKey.values()]
}

export function Tabs({ tabs, initialKey, orderStorageKey }: TabsProps) {
  const visible = useMemo(() => tabs.filter((t) => !t.hidden), [tabs])

  const [order, setOrder] = useState<string[]>(() => readSavedOrder(orderStorageKey))
  // The key being dragged is kept in a ref as well as state: the drop handler
  // must read the value set by dragstart even when both fire before React has
  // re-rendered, while the state copy drives the drag styling.
  const dragKeyRef = useRef<string | null>(null)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const ordered = useMemo(() => applyOrder(visible, order), [visible, order])

  const [active, setActive] = useState(initialKey ?? ordered[0]?.key)
  const [opened, setOpened] = useState<Set<string>>(new Set(active ? [active] : []))

  // A tab that disappears (permission change, entity type switch) must not leave
  // the panel blank — fall back to the first one that is still there.
  useEffect(() => {
    if (ordered.length > 0 && !ordered.some((t) => t.key === active)) {
      const next = ordered[0].key
      setActive(next)
      setOpened((prev) => (prev.has(next) ? prev : new Set(prev).add(next)))
    }
  }, [ordered, active])

  const select = (key: string) => {
    setActive(key)
    setOpened((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  }

  const persist = (next: string[]) => {
    setOrder(next)
    if (!orderStorageKey) return
    try {
      localStorage.setItem(orderStorageKey, JSON.stringify(next))
    } catch {
      // A full or blocked storage must not break reordering for this session.
    }
  }

  /** Moves the dragged tab in front of the tab it was dropped on. */
  const handleDrop = (dropKey: string) => {
    const moving = dragKeyRef.current
    const clear = () => {
      dragKeyRef.current = null
      setDragKey(null)
      setDragOverKey(null)
    }
    if (!moving || moving === dropKey) {
      clear()
      return
    }
    const keys = ordered.map((t) => t.key)
    const from = keys.indexOf(moving)
    const to = keys.indexOf(dropKey)
    if (from === -1 || to === -1) {
      clear()
      return
    }
    keys.splice(from, 1)
    keys.splice(to, 0, moving)
    persist(keys)
    clear()
  }

  const resetOrder = () => {
    if (orderStorageKey) {
      try {
        localStorage.removeItem(orderStorageKey)
      } catch {
        // ignore
      }
    }
    setOrder([])
  }

  const reorderable = !!orderStorageKey
  const activeTab = ordered.find((t) => t.key === active)

  return (
    <div className="activity-details">
      <ul className="nav-tabs" role="tablist">
        {ordered.map((tab) => (
          <li key={tab.key} role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={tab.key === active}
              className={[
                tab.key === active ? 'active' : '',
                reorderable ? 'draggable' : '',
                dragKey === tab.key ? 'dragging' : '',
                dragOverKey === tab.key && dragKey !== tab.key ? 'drag-over' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              draggable={reorderable}
              onDragStart={(e) => {
                if (!reorderable) return
                dragKeyRef.current = tab.key
                setDragKey(tab.key)
                e.dataTransfer.effectAllowed = 'move'
                // Firefox only starts a drag when data is set.
                e.dataTransfer.setData('text/plain', tab.key)
              }}
              onDragOver={(e) => {
                if (!reorderable) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                if (dragOverKey !== tab.key) setDragOverKey(tab.key)
              }}
              onDrop={(e) => {
                if (!reorderable) return
                e.preventDefault()
                handleDrop(tab.key)
              }}
              onDragEnd={() => {
                dragKeyRef.current = null
                setDragKey(null)
                setDragOverKey(null)
              }}
              onClick={() => select(tab.key)}
              title={reorderable ? `${tab.label} — drag to reorder` : tab.label}
            >
              <TabIcon name={(tab.icon ?? tab.key) as TabIconName} />
              <span>{tab.label}</span>
            </button>
          </li>
        ))}

        {reorderable && order.length > 0 && (
          <li className="nav-tabs-reset" role="presentation">
            <button type="button" className="link-btn" onClick={resetOrder} title="Restore the default tab order">
              Reset order
            </button>
          </li>
        )}
      </ul>
      <div className="tab-content" role="tabpanel">
        {activeTab && opened.has(activeTab.key) ? (
          // Keyed so switching tabs clears a previous tab's error state.
          <ErrorBoundary key={activeTab.key} label={activeTab.label}>
            {activeTab.render()}
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  )
}
